import { useMemo } from "react";
import { Grid2, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useStudents } from "../../../../context/students";
import ProfileQuestionnaire from "../../../../components/ProfileQuestionnaire";
import useStatuses from "../../../../context/status/useStatuses";
import LockedPage from "../../../../components/LockedPage";
import axiosManager from "../../../../services/axiosManager";
import Preloader from "../../../../components/Preloader";
import { useToast } from "../../../../context/toast";
import { useFeatures } from "../../../../context/features";

const StudentProfileForm = () => {
    const toast = useToast();
    const { t } = useTranslation();
    const { activeStudentId } = useStudents();
    const { showMentorPathway } = useStatuses();
    const { isFeatureEnabled } = useFeatures();

    const showProfileRealForm = isFeatureEnabled("_parent.mentorpathwayprofile.enabled");

    const {
        isLoading, isFetching, data: initialValues, refetch,
    } = useQuery({
        queryKey: ["mentorpathway/profile", activeStudentId],
        queryFn: () => axiosManager.get$(`/parent/api/students/${activeStudentId}/mentorpathway/profile`).then((response) => response?.data),
        retry: 0,
        enabled: !showProfileRealForm,
    });

    const sections = useMemo(() => [
        {
            id: "childAsLearner",
            title: t("studentProfileForm.childAsLearnerSection"),
            estimatedTime: 4,
            questions: [
                {
                    id: "childLearningStrengths",
                    title: t("studentProfileForm.childLearningStrengths.title"),
                    description: t("studentProfileForm.childLearningStrengths.description"),
                },
                {
                    id: "childDifficultiesAndHelp",
                    title: t("studentProfileForm.childDifficultiesAndHelp.title"),
                    description: t("studentProfileForm.childDifficultiesAndHelp.description"),
                },
                {
                    id: "howChildLearns",
                    title: t("studentProfileForm.howChildLearns.title"),
                    description: t("studentProfileForm.howChildLearns.description"),
                },
            ],
        },
        {
            id: "context",
            title: t("studentProfileForm.contextSection"),
            estimatedTime: 3,
            questions: [
                {
                    id: "familySituation",
                    title: t("studentProfileForm.familySituation.title"),
                    description: t("studentProfileForm.familySituation.description"),
                },
                {
                    id: "outsideSchoolCommitments",
                    title: t("studentProfileForm.outsideSchoolCommitments.title"),
                    description: t("studentProfileForm.outsideSchoolCommitments.description"),
                },
            ],
        },
        {
            id: "hopingFor",
            title: t("studentProfileForm.hopingForSection"),
            estimatedTime: 4,
            questions: [
                {
                    id: "reasonForChoosingMentoredPathway",
                    title: t("studentProfileForm.reasonForChoosingMentoredPathway.title"),
                    description: t("studentProfileForm.reasonForChoosingMentoredPathway.description"),
                },
                {
                    id: "childSuccessVision",
                    title: t("studentProfileForm.childSuccessVision.title"),
                    description: t("studentProfileForm.childSuccessVision.description"),
                },
            ],
        },
    ], [t]);

    const { mutateAsync: saveProfile, isPending: isSavingProfile } = useMutation({
        mutationFn: (data) => axiosManager.put$(`/parent/api/students/${activeStudentId}/mentorpathway/profile`, data).then((response) => response?.data),
        onError: () => toast.error(t("common.errorMessage")),
        onSuccess: () => toast.success(t("mentorPathway.saveSuccessMessage")),
    });

    const onSaveAndContinue = async (values) => {
        await saveProfile(values);
        refetch();
    };

    return (
        <div className="page-content">
            <div className="student-head-section" />
            <Grid2
                container
                spacing={2}
                className={`mb-12 ${showMentorPathway || !showProfileRealForm ? "min-head-section-height--small" : "min-head-section-height"}`}
            >
                <Grid2 size={{ xs: 12 }} className="text-white">
                    <Breadcrumbs
                        breadcrumbs={[
                            { label: t("navigation.home"), href: `/home/${activeStudentId}` },
                            { label: t("mentorPathway.title"), href: `/mentor-pathway/${activeStudentId}` },
                            { label: t("studentProfileForm.title"), current: true },
                        ]}
                    />
                    <Typography variant="h1" className="text-white">
                        {t("studentProfileForm.title")}
                    </Typography>
                    <Typography variant="body1" className="mt-4 text-white" component="div">
                        {t("studentProfileForm.subtitle")}
                    </Typography>
                </Grid2>
            </Grid2>
            {showProfileRealForm ? (
                <LockedPage showPermissionText isLocked={!showMentorPathway}>
                    <Preloader isLoading={isLoading}>
                        <Preloader isLoading={isFetching || isSavingProfile} overflow showInitiallyOverflow>
                            <ProfileQuestionnaire
                                title={t("studentProfileForm.title")}
                                description={t("studentProfileForm.description")}
                                sections={sections}
                                initialValues={initialValues}
                                isSaving={isSavingProfile}
                                onSaveAndContinue={onSaveAndContinue}
                            />
                        </Preloader>
                    </Preloader>
                </LockedPage>
            ) : (
                <div className="embedded-form-container">
                    <LockedPage showPermissionText isLocked={!showMentorPathway}>
                        <iframe
                            title="Student Profile form"
                            width="100%"
                            height="100%"
                            className="embedded-form"
                            src="https://forms.cloud.microsoft/e/VzqdceQ2WT?embed=true"
                            style={{ border: "none", maxWidth: "100%", maxHeight: "100%" }}
                            allowFullScreen
                        />
                    </LockedPage>
                </div>
            )}
        </div>
    );
};

export default StudentProfileForm;
